import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Pencil, X, Check, AlertCircle } from "lucide-react";
import { readGeoFromImage } from "@/utils/exifGeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Ändringsförslag-knapp + modal.
 * Build 90: visas universellt på alla publika datensätt (princip:
 * "Jeder öffentliche Datensatz soll grundsätzlich korrigierbar sein").
 * Detta inkluderar inloggade master/admin — knappen göms ej längre,
 * eftersom det skapade återkommande "knapp saknas"-förvirring.
 *
 * Props:
 *   typ: "foretag" | "forening" | "vardagskort" | "evenemang" | "anslag" | "karttipps" | "omrade" | "led" | "karta"
 *   referensId: id of the doc (eller slug för statisk innehåll som omrade/led/karta)
 *   referensNamn: display name of the doc
 */
export default function AndringsforslagButton({ typ, referensId, referensNamn }) {
  // Hooks MÅSTE deklareras innan ev. early return (react-hooks/rules-of-hooks)
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    meddelande: "",
    foreslagen_adress: "",
    foreslagen_plus_code: "",
    bild_url: "",
    avsandare_namn: "",
    avsandare_epost: "",
    avsandare_telefon: "",
    samtycke: false,
  });

  // Build 90 — visa knappen ALLTID. Inget user-baserat early return.

  const handleBildUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setError("Bilden är för stor (max 10 MB).");
      return;
    }
    setUploading(true);
    setError("");
    try {
      // Läs EXIF-GPS innan upload (om bilden har det) → auto-fyll Plus-Code
      const geo = await readGeoFromImage(file);
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((p) => ({
        ...p,
        bild_url: res.data.url || "",
        // Bara fyll i Plus-Code om användaren inte redan skrivit en
        ...(geo && !p.foreslagen_plus_code ? { foreslagen_plus_code: geo.plusCode } : {}),
      }));
    } catch (err) {
      setError("Kunde inte ladda upp bilden – försök igen.");
    }
    setUploading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.meddelande.trim().length < 10) {
      setError("Beskriv kort vad som behöver ändras (minst 10 tecken).");
      return;
    }
    if (!form.avsandare_namn.trim()) {
      setError("Ditt namn behövs så att redaktionen kan återkoppla.");
      return;
    }
    if (!form.samtycke) {
      setError("Markera samtycket för att skicka in.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/andringsforslag/submit`, {
        typ,
        referens_id: referensId,
        referens_namn: referensNamn,
        ...form,
      });
      setSubmitted(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Kunde inte skicka. Försök igen.");
    }
    setLoading(false);
  };

  const close = () => {
    setOpen(false);
    setTimeout(() => {
      setSubmitted(false);
      setError("");
      setForm({
        meddelande: "",
        foreslagen_adress: "",
        foreslagen_plus_code: "",
        bild_url: "",
        avsandare_namn: "",
        avsandare_epost: "",
        avsandare_telefon: "",
        samtycke: false,
      });
    }, 200);
  };

  return (
    <>
      <button
        type="button"
        data-testid={`andra-btn-${referensId}`}
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 text-xs text-bark/50 hover:text-falu no-underline transition-colors"
      >
        <Pencil size={12} /> Föreslå ändring
      </button>

      {open && (
        <div
          data-testid="andra-modal"
          className="fixed inset-0 z-[100] bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={close}
        >
          <div
            className="bg-bone border border-warm-border rounded-sm w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between p-5 sm:p-6 border-b border-warm-border">
              <div>
                <p className="font-body text-xs font-semibold text-bark/50 uppercase tracking-[0.2em] mb-1">
                  Föreslå ändring
                </p>
                <h3 className="font-heading text-xl font-semibold text-ink">{referensNamn}</h3>
              </div>
              <button
                type="button"
                data-testid="andra-close"
                onClick={close}
                aria-label="Stäng"
                className="p-1 text-bark hover:text-ink transition-colors"
              >
                <X size={22} />
              </button>
            </div>

            {submitted ? (
              <div className="p-6 sm:p-8 text-center">
                <Check size={40} className="mx-auto text-pine mb-3" />
                <h4 className="font-heading text-lg font-semibold text-ink mb-2">Tack!</h4>
                <p className="text-sm text-bark leading-relaxed mb-6">
                  Ditt ändringsförslag har skickats till redaktionen. Vi hör av oss om vi behöver
                  komplettera uppgifterna.
                </p>
                <button
                  type="button"
                  data-testid="andra-close-success"
                  onClick={close}
                  className="bg-pine hover:bg-pine-hover text-parchment font-body font-semibold text-sm px-5 py-2.5 rounded-sm transition-colors"
                >
                  Stäng
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="p-5 sm:p-6 flex flex-col gap-4">
                <p className="text-sm text-bark leading-relaxed">
                  Är uppgifterna fel, gamla eller ofullständiga? Skriv vad som behöver ändras så
                  granskar redaktionen och uppdaterar. Ingen inloggning krävs.
                </p>

                <Field label="Vad ska ändras? *" testid="andra-meddelande">
                  <textarea
                    data-testid="andra-meddelande-input"
                    value={form.meddelande}
                    onChange={(e) => setForm((p) => ({ ...p, meddelande: e.target.value }))}
                    rows={5}
                    placeholder="T.ex. 'Telefonnumret är ändrat till 070-… ', 'Nya öppettider: mån–fre 9–17', 'Nytt e-post är …'"
                    className="w-full px-3 py-2.5 border border-warm-border rounded-sm bg-white text-sm focus:ring-2 focus:ring-pine focus:outline-none"
                    required
                  />
                </Field>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Föreslagen adress (frivilligt)" testid="andra-adress">
                    <input
                      data-testid="andra-adress-input"
                      type="text"
                      value={form.foreslagen_adress}
                      onChange={(e) => setForm((p) => ({ ...p, foreslagen_adress: e.target.value }))}
                      placeholder="T.ex. Storgatan 5, 880 30 Backe"
                      className="w-full px-3 py-2.5 border border-warm-border rounded-sm bg-white text-sm focus:ring-2 focus:ring-pine focus:outline-none"
                    />
                  </Field>
                  <Field label="Plus-Code (frivilligt)" testid="andra-plus-code">
                    <input
                      data-testid="andra-plus-code-input"
                      type="text"
                      value={form.foreslagen_plus_code}
                      onChange={(e) => setForm((p) => ({ ...p, foreslagen_plus_code: e.target.value }))}
                      placeholder="T.ex. R97X+WJ Backe"
                      className="w-full px-3 py-2.5 border border-warm-border rounded-sm bg-white text-sm focus:ring-2 focus:ring-pine focus:outline-none"
                    />
                    <p className="text-[11px] text-bark/60 mt-1">
                      Tips: laddar du upp en bild med GPS-data fyller vi i koden automatiskt.
                    </p>
                  </Field>
                </div>

                <Field label="Bifoga bild (frivilligt)" testid="andra-bild">
                  {form.bild_url ? (
                    <div className="flex items-center gap-3 bg-bone-alt/40 border border-warm-border rounded-sm p-2">
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${form.bild_url}`} alt="Bifogad bild" className="w-20 h-20 object-cover rounded-sm" />
                      <button
                        type="button"
                        data-testid="andra-bild-remove"
                        onClick={() => setForm((p) => ({ ...p, bild_url: "" }))}
                        className="text-xs text-falu hover:text-falu-hover underline"
                      >
                        Ta bort bild
                      </button>
                    </div>
                  ) : (
                    <label className="inline-flex items-center gap-2 text-sm text-pine hover:text-pine-hover cursor-pointer">
                      {uploading ? "Laddar upp..." : "Välj bild från enheten"}
                      <input
                        data-testid="andra-bild-input"
                        type="file"
                        accept="image/*"
                        onChange={handleBildUpload}
                        disabled={uploading}
                        className="hidden"
                      />
                    </label>
                  )}
                </Field>

                <Field label="Ditt namn *" testid="andra-namn">
                  <input
                    data-testid="andra-namn-input"
                    type="text"
                    value={form.avsandare_namn}
                    onChange={(e) => setForm((p) => ({ ...p, avsandare_namn: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-warm-border rounded-sm bg-white text-sm focus:ring-2 focus:ring-pine focus:outline-none"
                    required
                  />
                </Field>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="E-post" testid="andra-epost">
                    <input
                      data-testid="andra-epost-input"
                      type="email"
                      value={form.avsandare_epost}
                      onChange={(e) => setForm((p) => ({ ...p, avsandare_epost: e.target.value }))}
                      className="w-full px-3 py-2.5 border border-warm-border rounded-sm bg-white text-sm focus:ring-2 focus:ring-pine focus:outline-none"
                    />
                  </Field>
                  <Field label="Telefon" testid="andra-telefon">
                    <input
                      data-testid="andra-telefon-input"
                      type="tel"
                      value={form.avsandare_telefon}
                      onChange={(e) => setForm((p) => ({ ...p, avsandare_telefon: e.target.value }))}
                      className="w-full px-3 py-2.5 border border-warm-border rounded-sm bg-white text-sm focus:ring-2 focus:ring-pine focus:outline-none"
                    />
                  </Field>
                </div>
                <p className="text-xs text-bark/60 -mt-1">Minst en kontaktväg underlättar återkoppling (frivilligt).</p>

                <label className="flex items-start gap-2.5 text-sm text-ink cursor-pointer">
                  <input
                    data-testid="andra-samtycke-input"
                    type="checkbox"
                    checked={form.samtycke}
                    onChange={(e) => setForm((p) => ({ ...p, samtycke: e.target.checked }))}
                    className="mt-1"
                  />
                  <span>
                    {/* Etappe 4.8 — uppdaterad text med länkar */}
                    Jag bekräftar att jag har rätt att skicka in ändringen och att vidalv.live får
                    granska, redigera och publicera den enligt{" "}
                    <Link to="/villkor-inskickat" target="_blank" className="text-falu underline">villkoren</Link> och{" "}
                    <Link to="/integritet" target="_blank" className="text-falu underline">integritetspolicyn</Link>.
                  </span>
                </label>

                {/* Etappe 4.8 — dataskyddshinweis */}
                <p data-testid="andra-dataskydd-hint" className="text-xs text-bark/60 leading-relaxed -mt-1">
                  Vi använder dina uppgifter för att granska förslaget och kontakta dig vid frågor.
                </p>

                {error && (
                  <div data-testid="andra-error" className="flex items-start gap-2 bg-falu/5 border border-falu/20 rounded-sm p-3 text-sm text-falu">
                    <AlertCircle size={16} className="shrink-0 mt-0.5" /> <span>{error}</span>
                  </div>
                )}

                <div className="flex flex-wrap gap-3 mt-2">
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="andra-submit"
                    className="bg-falu hover:bg-falu-hover text-parchment font-body font-semibold text-sm px-5 py-2.5 rounded-sm transition-colors disabled:opacity-50"
                  >
                    {loading ? "Skickar..." : "Skicka förslag"}
                  </button>
                  <button
                    type="button"
                    onClick={close}
                    className="text-sm text-bark hover:text-ink transition-colors"
                  >
                    Avbryt
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function Field({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <label className="block text-sm font-medium text-ink mb-1">{label}</label>
      {children}
    </div>
  );
}
